// src/components/DynamicMenu/index.js
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Popover, Tooltip, Tour } from "antd";
import {
  DashboardOutlined,
  ScheduleOutlined,
  HomeOutlined,
  ClearOutlined,
  ToolOutlined,
  SettingOutlined,
  FileTextOutlined,
  DownOutlined,
  SearchOutlined,
  CloseCircleFilled,
} from "@ant-design/icons";
import { useAuth } from "../../context/AuthContext";
import useDynamicModules from "../../hooks/useDynamicModules";
import hub from "../../services/EventHub";
import menuIconMap from "../menuIconMap";
import menuLabelMap from "../menuLabelMap";
import "../SideMenu/sidebar.css";

/* ---- 1) Group definitions (icon + label) ------------------- */

export const GROUP_DEFS = {
  dashboard: { key: "dashboard", label: "Dashboard", icon: DashboardOutlined },
  frontOffice: { key: "frontOffice", label: "Front Office", icon: ScheduleOutlined },
  rooms: { key: "rooms", label: "Rooms", icon: HomeOutlined },
  housekeeping: { key: "housekeeping", label: "Housekeeping", icon: ClearOutlined },
  maintenance: { key: "maintenance", label: "Maintenance", icon: ToolOutlined },
  admin: { key: "admin", label: "Administration", icon: SettingOutlined },
  other: { key: "other", label: "Other", icon: FileTextOutlined },
};

/**
 * Map each backend component name to a group key.
 * NOTE: mapping is case-sensitive; match backend `component` exactly.
 */
export const COMPONENT_GROUP_MAP = {
  // Dashboard
  Dashboard: "dashboard",
  AdminDashboard: "dashboard",
 
  // Front Office
  BookingPage: "frontOffice",
  BookingListPage: "frontOffice",
  CheckInOutPage: "frontOffice",
  CheckInPage: "frontOffice",
  CheckOutPage: "frontOffice",
  CheckIn: "frontOffice",
  CheckOut: "frontOffice",
  ReservationCalendarPage: "frontOffice",
  ReservationGantt: "frontOffice",
  GuestListPage: "frontOffice",
  GuestProfilesPage: "frontOffice",
  RestaurantOrderPage: "frontOffice",
  CreateServicePage: "frontOffice",

  // Rooms
  RoomTypesPage: "rooms",
  RoomPricePage: "rooms",
  RoomListPage: "rooms",
  RoomStatusPage: "rooms",
  RoomImagesPage: "rooms",
  BedTypesPage: "rooms",
  FloorPlansPage: "rooms",

  // Housekeeping
  HousekeeperDashboard: "housekeeping",
  HousekeepingPage: "housekeeping",
  HousekeepingReportsPage: "housekeeping",

  // Maintenance
  MaintenanceDashboard: "maintenance",
  MaintenancePage: "maintenance",
  MaintenanceRequestForm: "maintenance",

  // Admin / Platform
  ManageUsers: "admin",
  AddUserPage: "admin",
  ManageStaffPage: "admin",
  ModuleAssignment: "admin",
  PermissionManager: "admin",
  SettingsPage: "admin",
  HotelSettings: "admin",
  SetupHotelPage: "admin",
  KycReviewPage: "admin",
  KYCVerificationPage: "admin",
  KYCUploadPage: "admin",
  SystemLogPage: "admin",
  SupportAdminPage: "admin",
  AdminLegalDocsPage: "admin",
  
  HotelAffiliationManagementPage: "admin",
  HotelAgentApplicationsPage: "admin",
  HotelAgentKycPage: "admin",
  HotelAgentCommissionsPage: "admin",
  AllAgentsKycPage: "admin",
  HotelSlaPage: "admin",
};

/**
 * Ordering inside groups (lower = earlier).
 * Rooms order = Room Type, Room Price, Room List, Room Status.
 * Front Office: Reservation first (per request).
 */
export const GROUP_ITEM_ORDER = {
  // Front Office (Reservation first)
  ReservationCalendarPage: 1,
  ReservationGantt: 2,

  BookingPage: 3,
  BookingListPage: 4,

  CheckInOutPage: 5,
  CheckIn: 5,
  CheckInPage: 5,

  CheckOut: 6,
  CheckOutPage: 6,

  GuestListPage: 10,
  GuestProfilesPage: 11,
  RestaurantOrderPage: 20,
  CreateServicePage: 30,

  // Rooms (requested order)
  RoomTypesPage: 1,
  RoomPricePage: 2,
  RoomListPage: 3,
  RoomStatusPage: 4,

  // Remaining Rooms
  RoomImagesPage: 10,
  BedTypesPage: 20,
  FloorPlansPage: 30,
  HotelSlaPage: 50,
};

/**
 * Groups that should show right-aligned numeric order badges (not part of label text).
 */
const ORDER_BADGE_GROUPS = new Set(["rooms"]);

/**
 * Rooms setup tour (first login) version.
 * Bump the version to re-run tour for everyone if needed later.
 */
const ROOMS_TOUR_VERSION = "v1";

/* ---- helpers for route matching --------------------------- */

const normalize = (p) => (p === "/" ? "/" : (p || "").replace(/\/+$/, ""));

const matches = (pathname, itemPath) =>
  itemPath === "/"
    ? pathname === "/"
    : pathname === itemPath || pathname.startsWith(itemPath + "/");

/* ================== DynamicMenu ============================ */

const OPEN_STORAGE_KEY = "hms.sidebar.open";

const DynamicMenu = ({ showLabels }) => {
  const queryClient = useQueryClient();
  const { authData } = useAuth();
  const { data: modules = [], isLoading, error } = useDynamicModules();
  const location = useLocation();
  const navigate = useNavigate();

  /* 1. Permissions refresh from EventHub */
  useEffect(() => {
    const unsubscribe = hub.subscribe("permissionsChanged", () => {
      queryClient.invalidateQueries({
        queryKey: ["modules", authData?.roleId, authData?.hotelId],
      });
    });
    return unsubscribe;
  }, [queryClient, authData?.roleId, authData?.hotelId]);

  /* 2. Flatten modules -> menu items (with groupKey) */
  const items = useMemo(() => {
    if (!modules.length) return [];

    return modules.map((m) => {
      const component = m.component;
      const groupKey = COMPONENT_GROUP_MAP[component] || "other";
      const IconComp = menuIconMap[m.icon ?? component] ?? FileTextOutlined;

      const rawPath = m.path ?? "";
      const to = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;

      return {
        key: m.moduleId ?? m.path ?? component,
        to,
        label: menuLabelMap[component] ?? m.friendlyName ?? component,
        icon: IconComp,
        component,
        groupKey,
      };
    });
  }, [modules]);

  /** component -> route path (used by the Rooms setup tour) */
  const pathByComponent = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      if (it?.component && it?.to) map.set(it.component, it.to);
    }
    return map;
  }, [items]);

  /* 3. Group the items by groupKey + stable ordering + order badges */
  const groups = useMemo(() => {
    const map = new Map();

    for (const item of items) {
      const def = GROUP_DEFS[item.groupKey] ?? GROUP_DEFS.other;
      if (!map.has(def.key)) {
        map.set(def.key, { key: def.key, label: def.label, Icon: def.icon, items: [] });
      }
      map.get(def.key).items.push(item);
    }

    for (const g of map.values()) {
      g.items.sort((a, b) => {
        const wa = GROUP_ITEM_ORDER[a.component] ?? 999;
        const wb = GROUP_ITEM_ORDER[b.component] ?? 999;
        if (wa !== wb) return wa - wb;
        return (a.label || "").localeCompare(b.label || "");
      });

      g.items = ORDER_BADGE_GROUPS.has(g.key)
        ? g.items.map((it, idx) => ({ ...it, orderNo: idx + 1 }))
        : g.items.map((it) => ({ ...it, orderNo: null }));
    }

    const ordered = [];
    Object.keys(GROUP_DEFS).forEach((k) => {
      const g = map.get(k);
      if (g && g.items.length) ordered.push(g);
    });
    for (const g of map.values()) {
      if (!ordered.find((x) => x.key === g.key)) ordered.push(g);
    }
    return ordered;
  }, [items]);

  /* 4. Active group + item from the current route */
  const active = useMemo(() => {
    const pathname = normalize(location.pathname);
    let best = null;
    let bestLen = -1;

    groups.forEach((g) => {
      g.items.forEach((it) => {
        const p = normalize(it.to);
        if (matches(pathname, p) && p.length > bestLen) {
          bestLen = p.length;
          best = { groupKey: g.key, itemKey: it.key };
        }
      });
    });
    return best;
  }, [groups, location.pathname]);

  /* 5. Accordion: one group open at a time (remembered per browser) */
  const [openKey, setOpenKey] = useState(() => {
    try {
      return localStorage.getItem(OPEN_STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });

  const setOpen = (key) => {
    setOpenKey(key);
    try {
      if (key) localStorage.setItem(OPEN_STORAGE_KEY, key);
      else localStorage.removeItem(OPEN_STORAGE_KEY);
    } catch {
      /* storage unavailable */
    }
  };

  const toggleGroup = (key) => setOpen(openKey === key ? null : key);

  // Navigating into a group opens it (and closes the previous one).
  useEffect(() => {
    if (active?.groupKey && active.groupKey !== "dashboard") setOpen(active.groupKey);
  }, [active?.groupKey]);

  /* 5b. Menu search */
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const visibleGroups = useMemo(() => {
    if (!q) return groups;
    return groups
      .map((g) => ({ ...g, items: g.items.filter((it) => (it.label || "").toLowerCase().includes(q)) }))
      .filter((g) => g.items.length > 0);
  }, [groups, q]);

  const onSearchKeyDown = (e) => {
    if (e.key === "Escape") setQuery("");
    if (e.key === "Enter") {
      const first = visibleGroups[0]?.items?.[0];
      if (first?.to) {
        navigate(first.to);
        setQuery("");
      }
    }
  };

  /* 6. First-login Rooms Setup Tour */
  const [roomsTourOpen, setRoomsTourOpen] = useState(false);
  const [roomsTourStep, setRoomsTourStep] = useState(0);

  const roomsTourStorageKey = useMemo(() => {
    const uid = authData?.userId ?? "anon";
    const hid = authData?.hotelId ?? "0";
    return `hms:onboarding:roomsSetup:${ROOMS_TOUR_VERSION}:${hid}:${uid}`;
  }, [authData?.userId, authData?.hotelId]);

  const hasRoomsSetupModules = useMemo(() => {
    return (
      pathByComponent.has("RoomTypesPage") ||
      pathByComponent.has("RoomPricePage") ||
      pathByComponent.has("RoomListPage")
    );
  }, [pathByComponent]);

  useEffect(() => {
    if (!showLabels) return;
    if (!items.length) return;
    if (!hasRoomsSetupModules) return;

    const seen = localStorage.getItem(roomsTourStorageKey);
    if (seen) return;

    setOpen("rooms");
    setRoomsTourStep(0);
    setRoomsTourOpen(true);
  }, [showLabels, items.length, roomsTourStorageKey, hasRoomsSetupModules]);

  const closeRoomsTour = () => {
    localStorage.setItem(roomsTourStorageKey, "1");
    setRoomsTourOpen(false);
  };

  const goToComponent = (componentName) => {
    const to = pathByComponent.get(componentName);
    if (to) navigate(to);
  };

  const handleRoomsTourChange = (nextStep) => {
    setRoomsTourStep(nextStep);
    setOpen("rooms");

    if (nextStep === 2) goToComponent("RoomTypesPage");
    if (nextStep === 4) goToComponent("RoomPricePage");
    if (nextStep === 6) goToComponent("RoomListPage");
  };

  const roomsTourSteps = useMemo(() => {
    return [
      {
        title: "Rooms Setup (First Time)",
        description:
          "To start using Rooms, follow this order: 1) Room Types, 2) Room Prices, 3) Room List (create rooms).",
        target: () => document.getElementById("tour-group-rooms"),
        placement: "right",
      },
      {
        title: "Step 1: Room Types",
        description: "Define your room types first (e.g., Standard, Deluxe, Suite).",
        target: () => document.getElementById("tour-menu-RoomTypesPage"),
        placement: "right",
      },
      {
        title: "Create a Room Type",
        description: "Click the Add/New button to create your first room type.",
        target: () => document.getElementById("tour-roomtypes-add"),
        placement: "bottom",
      },
      {
        title: "Step 2: Room Prices",
        description: "Set prices for your room types (per night, seasonal rates, etc.).",
        target: () => document.getElementById("tour-menu-RoomPricePage"),
        placement: "right",
      },
      {
        title: "Create a Price",
        description: "Add a price/rate for at least one room type.",
        target: () => document.getElementById("tour-roomprice-add"),
        placement: "bottom",
      },
      {
        title: "Step 3: Room List",
        description: "Now create the actual rooms and assign their type (Room 101, Room 102...).",
        target: () => document.getElementById("tour-menu-RoomListPage"),
        placement: "right",
      },
      {
        title: "Create a Room",
        description: "Add your rooms here (number, type, floor, etc.).",
        target: () => document.getElementById("tour-roomlist-add"),
        placement: "bottom",
      },
    ];
  }, []);

  /* 7. Loading / error / empty states */
  if (isLoading) return <div className="hms-nav-state">Loading…</div>;
  if (error) return <div className="hms-nav-state is-error">Failed to load menu</div>;
  if (!items.length) return <div className="hms-nav-state">No items</div>;

  /* 8. UI */
  const tourId = (component) =>
    component === "RoomTypesPage"
      ? "tour-menu-RoomTypesPage"
      : component === "RoomPricePage"
      ? "tour-menu-RoomPricePage"
      : component === "RoomListPage"
      ? "tour-menu-RoomListPage"
      : undefined;

  const renderSubItem = (it) => (
    <li key={it.key}>
      <NavLink
        id={tourId(it.component)}
        to={it.to}
        end
        title={it.label}
        className={({ isActive }) => `hms-nav-item ${isActive ? "is-active" : ""}`}
      >
        <span className="hms-nav-label">{it.label}</span>
        {it.orderNo ? (
          <span className="hms-nav-order" title={`Step ${it.orderNo}`}>
            {it.orderNo}
          </span>
        ) : null}
      </NavLink>
    </li>
  );

  /* ---- collapsed rail: one icon per group, items in a flyout ---- */
  if (!showLabels) {
    return (
      <nav className="hms-nav is-collapsed">
        {groups.map((g) => {
          const isCurrent = active?.groupKey === g.key;
          const Icon = g.Icon;

          if (g.key === "dashboard") {
            const only = g.items[0];
            return (
              <Tooltip key={g.key} title={g.label} placement="right" arrow>
                <NavLink
                  to={only.to}
                  end
                  className={({ isActive }) => `hms-nav-top ${isActive ? "is-active" : ""}`}
                  aria-label={g.label}
                >
                  <span className="hms-nav-icon">
                    <Icon />
                  </span>
                </NavLink>
              </Tooltip>
            );
          }

          const content = (
            <div className="hms-flyout-body">
              <div className="hms-flyout-title">{g.label}</div>
              <ul className="hms-nav-sub is-flyout">{g.items.map(renderSubItem)}</ul>
            </div>
          );

          return (
            <Popover
              key={g.key}
              content={content}
              placement="rightTop"
              trigger="hover"
              mouseEnterDelay={0.05}
              arrow={false}
              overlayClassName="hms-flyout"
            >
              <button
                type="button"
                className={`hms-nav-group ${isCurrent ? "is-current" : ""}`}
                aria-label={g.label}
                onClick={() => g.items[0]?.to && navigate(g.items[0].to)}
              >
                <span className="hms-nav-icon">
                  <Icon />
                </span>
              </button>
            </Popover>
          );
        })}
      </nav>
    );
  }

  /* ---- expanded ---- */
  return (
    <>
      <div className="hms-nav-search-wrap">
        <SearchOutlined className="hms-nav-search-icon" />
        <input
          type="text"
          className="hms-nav-search"
          placeholder="Search menu"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          aria-label="Search menu"
        />
        {query && (
          <CloseCircleFilled className="hms-nav-search-clear" onClick={() => setQuery("")} />
        )}
      </div>

      <nav className="hms-nav is-expanded">
        {visibleGroups.length === 0 && <div className="hms-nav-state">No matches</div>}

        {visibleGroups.map((g) => {
          const Icon = g.Icon;
          const isCurrent = active?.groupKey === g.key;

          if (g.key === "dashboard") {
            return g.items.map((it) => (
              <NavLink
                key={it.key}
                to={it.to}
                end
                className={({ isActive }) => `hms-nav-top ${isActive ? "is-active" : ""}`}
              >
                <span className="hms-nav-icon">
                  <Icon />
                </span>
                <span className="hms-nav-label">{it.label}</span>
              </NavLink>
            ));
          }

          const open = q ? true : openKey === g.key;

          return (
            <div key={g.key} className="hms-nav-block">
              <button
                id={g.key === "rooms" ? "tour-group-rooms" : undefined}
                type="button"
                className={`hms-nav-group ${isCurrent ? "is-current" : ""}`}
                aria-expanded={open}
                onClick={() => toggleGroup(g.key)}
              >
                <span className="hms-nav-icon">
                  <Icon />
                </span>
                <span className="hms-nav-label">{g.label}</span>
                <DownOutlined className={`hms-nav-chevron ${open ? "is-open" : ""}`} />
              </button>

              <div className={`hms-nav-collapse ${open ? "is-open" : ""}`}>
                <div className="hms-nav-sub-wrap">
                  <ul className="hms-nav-sub">{g.items.map(renderSubItem)}</ul>
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      <Tour
        open={roomsTourOpen}
        current={roomsTourStep}
        onChange={handleRoomsTourChange}
        onClose={closeRoomsTour}
        steps={roomsTourSteps}
        mask
      />
    </>
  );
};

export default React.memo(DynamicMenu);
